import { deepEqual } from 'volta/utils/object-utils';

import { helper } from '@ember/component/helper';
import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

/**
 * @param state - initial state to use, undefined if not provided.
 *
 * @returns - Object with a `state` property and `setState` method to set the
 *            `state`
 */
export class SingleUseState<T> {
  @tracked state: T;
  @tracked isDirty: boolean = false;
  private readonly _initialState: T;

  constructor(state: T) {
    this.state = state;
    this._initialState = state;
  }

  @action
  setState(state: T): T {
    this.isDirty = !deepEqual(state, this._initialState);
    this.state = state;
    return state;
  }

  @action
  set(fieldPath: string | undefined, value: any) {
    if (!fieldPath || typeof this.state !== 'object') {
      this.setState(value as T);
    } else {
      const newState = Object.assign({}, this.state);
      set(newState, fieldPath, value);
      this.setState(newState);
    }
    return this.state;
  }
}

export function useState<T>([initialValue]: [T]): SingleUseState<T> {
  return new SingleUseState(initialValue);
}

const useStateHelper = helper(useState);

export default useStateHelper;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'use-state': typeof useStateHelper;
  }
}
